import { CalendarDate, DateDuration } from '@internationalized/date';
import { ComponentProps, useMemo } from 'react';
import { RangeCalendar, RangeCalendarProps } from 'react-aria-components';

import ErrorList from '../../types/UI/ErrorList';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

import BaseCalendar from './components/BaseCalendar/BaseCalendar';
import DetailLevel from './types/DetailLevel';

import styles from './DateCalendar.module.css';

type DateRangeCalendarProps = {
    allowSingleDayRange?: boolean;
    errors?: ErrorList;

    defaultDetailLevel?: DetailLevel;
    maxDetailLevel?: DetailLevel;
    minDetailLevel?: DetailLevel;
} & Pick<
    RangeCalendarProps<CalendarDate>,
    | 'value'
    | 'maxValue'
    | 'minValue'
    | 'defaultValue'
    | 'onChange'
    | 'defaultFocusedValue'
> &
    Pick<
        ComponentProps<typeof BaseCalendar>,
        'singleDateButtonLabel' | 'visibleMonths' | 'onSelectingRangeChange'
    >;

const DateRangeCalendar = ({
    // Props for this component
    errors,
    allowSingleDayRange = false,
    defaultDetailLevel,
    maxDetailLevel,
    minDetailLevel,

    // Base calendar props
    singleDateButtonLabel,
    visibleMonths = 1,
    onSelectingRangeChange,

    // Range calendar props
    ...calendarProps
}: DateRangeCalendarProps) => {
    const visibleDuration: DateDuration = useMemo(
        () => ({ months: visibleMonths }),
        [visibleMonths]
    );

    return (
        <RangeCalendar
            visibleDuration={visibleDuration}
            className={styles.Root}
            pageBehavior="single"
            {...calendarProps}
        >
            <BaseCalendar
                visibleMonths={visibleMonths}
                singleDateButtonLabel={singleDateButtonLabel}
                onSelectingRangeChange={onSelectingRangeChange}
                defaultDetailLevel={defaultDetailLevel}
                maxDetailLevel={maxDetailLevel}
                minDetailLevel={minDetailLevel}
                mode={allowSingleDayRange ? 'singleOrRange' : 'range'}
            />
            <ErrorMessage>{errors}</ErrorMessage>
        </RangeCalendar>
    );
};

export default DateRangeCalendar;
